import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { FALLBACK_LANG } from './i18n.constants';
import { followSavedLangChanges, getLangStorage, readSavedLang } from './lang-preference';
import { resolveLang } from './resolve-lang.utils';

/** Loads the fallback language and then `lang`, so keys missing in `lang` already resolve from the fallback. */
export async function useLangWithFallback(translate: TranslateService, lang: string): Promise<void> {
  translate.setDefaultLang(FALLBACK_LANG);
  await firstValueFrom(translate.use(FALLBACK_LANG));
  if (lang !== FALLBACK_LANG) await firstValueFrom(translate.use(lang));
}

/**
 * APP_INITIALIZER: starts in the language the user saved (see `selectLang`), or else the first supported browser
 * language, follows choices made in other tabs and keeps `<html lang>` in sync with the current language. Translations
 * are loaded before the first render, so templates never show raw keys while loading.
 */
export function initializeI18nFactory(translate: TranslateService, document: Document): () => Promise<void> {
  return () => {
    translate.onLangChange.subscribe(({ lang }) => (document.documentElement.lang = lang));
    const view = document.defaultView;
    followSavedLangChanges(translate, view);
    const savedLang = readSavedLang(getLangStorage(view));
    return useLangWithFallback(translate, resolveLang([savedLang, ...getBrowserLangs(view?.navigator)]));
  };
}

function getBrowserLangs(navigator: Navigator | undefined): readonly string[] {
  if (!navigator) return [];
  return navigator.languages?.length ? navigator.languages : [navigator.language];
}
